import { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Menu, X, Home, PieChart, FileText, TrendingUp, Settings, LogIn, BarChart3, LogOut, UserCog, Eye, ListMusic, FlaskConical, ExternalLink, ScrollText, Database, CalendarClock, Gauge, Activity, SlidersHorizontal } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { api } from '../lib/api';
import type { AuthStatusResponse } from '../types/api';
import { MarketClosureTicker } from './MarketClosureTicker';
import { TradingModeBadge } from './TradingModeBadge';

interface LayoutProps {
  children: React.ReactNode;
}

interface NavItem {
  path: string;
  label: string;
  icon: React.ElementType;
}

interface NavGroup {
  label: string;
  items: NavItem[];
  adminOnly?: boolean;
}

const PORTFOLIO_ITEMS: NavItem[] = [
  { path: '/', label: 'Dashboard', icon: Home },
  { path: '/holdings', label: 'Holdings', icon: PieChart },
  { path: '/orders', label: 'Orders', icon: FileText },
  { path: '/equity', label: 'Equity Curve', icon: TrendingUp },
  { path: '/preview', label: 'Preview', icon: Eye },
];

// PB-1: Backtest, Attribution and Allocation Analysis are gone from here. They are research
// workflows — Lab's product, not Trade's — and they now live only in the Lab shell
// (`src/lab/LabLayout.tsx`).
//
// SDO-3: the surviving item is "Deployed Strategies", not "Strategies". The label is the boundary
// stated in the one place a user reads before clicking: Trade shows what it trades, and a strategy
// is written in Lab. The nine authoring and research calls the old page drove are gone; what is
// left is allocation, regime sizing and regime backfill, which Trade owns.
const ANALYSIS_ITEMS: NavItem[] = [
  { path: '/strategies', label: 'Deployed Strategies', icon: ListMusic },
];

/**
 * Where PortfoliYo Lab is reachable, read at build time.
 *
 * Deliberately **not** fetched from the Trade API. A product-switch link is a piece of
 * deployment topology, and asking the Trade backend for it would add a runtime dependency in the
 * one direction this milestone is trying to remove — Trade knowing anything about Lab beyond a
 * URL. Empty (the default) renders no link at all rather than a dead one, which is correct for a
 * single-process development checkout where no Lab origin exists.
 */
const LAB_ORIGIN = (import.meta.env.VITE_LAB_ORIGIN as string | undefined) ?? '';

const ADMIN_ITEMS: NavItem[] = [
  { path: '/settings', label: 'Settings', icon: Settings },
  { path: '/admin/invitations', label: 'Invitations', icon: UserCog },
  { path: '/admin/logs', label: 'Logs', icon: ScrollText },
  { path: '/admin/cache', label: 'Cache', icon: Database },
  { path: '/admin/scheduler', label: 'Scheduler', icon: CalendarClock },
  { path: '/admin/performance', label: 'Performance', icon: Gauge },
  { path: '/admin/regime-position-multipliers', label: 'Regime Multipliers', icon: SlidersHorizontal },
];

function NavSection({ group, currentPath, onNavigate }: {
  group: NavGroup;
  currentPath: string;
  onNavigate: () => void;
}) {
  return (
    <div>
      <p className="px-3 pt-3 pb-1 text-[10px] font-bold uppercase tracking-widest text-gray-400 select-none">
        {group.label}
      </p>
      {group.items.map((item) => {
        const isActive = currentPath === item.path;
        const Icon = item.icon;
        return (
          <Link
            key={item.path}
            to={item.path}
            onClick={onNavigate}
            className={`flex items-center gap-3 px-3 py-2.5 rounded-lg font-medium transition-all duration-200 text-sm ${
              isActive
                ? 'bg-emerald-700 text-white shadow-md'
                : 'text-gray-700 hover:bg-emerald-50 hover:text-emerald-800'
            }`}
          >
            <Icon className="w-4 h-4 flex-shrink-0" />
            <span>{item.label}</span>
          </Link>
        );
      })}
    </div>
  );
}

export function Layout({ children }: LayoutProps) {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [brokerAuthStatus, setBrokerAuthStatus] = useState<AuthStatusResponse | undefined>(undefined);
  const [regimeDashboardEnabled, setRegimeDashboardEnabled] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { user, isAdmin, logout: authLogout } = useAuth();

  useEffect(() => {
    const loadBrokerAuthStatus = async () => {
      try {
        const status = await api.getAuthStatus();
        setBrokerAuthStatus(status);
      } catch (error) {
        console.error('Error loading broker auth status:', error);
      }
    };

    loadBrokerAuthStatus();
    const interval = setInterval(loadBrokerAuthStatus, 30000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    api.getRegimeDashboardStatus()
      .then((flags) => setRegimeDashboardEnabled(flags.dashboard_enabled && flags.engine_enabled))
      .catch(() => setRegimeDashboardEnabled(false));
  }, []);

  const analysisItems = regimeDashboardEnabled
    ? [...ANALYSIS_ITEMS, { path: '/regime-scores', label: 'Regime Scores', icon: Activity }]
    : ANALYSIS_ITEMS;

  const handleLogout = async () => {
    await authLogout();
    navigate('/login');
  };

  const close = () => setSidebarOpen(false);

  return (
    <div className="min-h-screen">
      <button
        onClick={() => setSidebarOpen(!sidebarOpen)}
        className="lg:hidden fixed top-4 left-4 z-50 p-2.5 bg-white rounded-xl shadow-lg border border-gray-200 hover:bg-gray-50 transition-colors"
        aria-label="Toggle menu"
      >
        {sidebarOpen ? <X className="w-5 h-5 text-gray-700" /> : <Menu className="w-5 h-5 text-gray-700" />}
      </button>

      <aside
        className={`fixed inset-y-0 left-0 z-40 w-64 bg-white border-r border-gray-200 shadow-xl transform transition-transform duration-300 ease-out lg:translate-x-0 ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        <div className="flex flex-col h-full">
          {/* Header */}
          <div className="p-5 border-b border-gray-200">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-emerald-700 flex items-center justify-center shadow-lg">
                <BarChart3 className="w-5 h-5 text-white" />
              </div>
              <div>
                <h1 className="text-lg font-bold text-gray-900">PortfoliYo</h1>
                <p className="text-xs font-medium text-emerald-700">PortfoliYo Trade</p>
              </div>
            </div>

            {user && (
              <div className="mt-3 pt-3 border-t border-gray-200 space-y-2">
                <div className="flex items-center gap-2 text-xs text-gray-600">
                  <span className="truncate">{user.email}</span>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  {isAdmin && (
                    <span className="inline-block px-2 py-0.5 text-[10px] font-bold rounded bg-emerald-100 text-emerald-800 uppercase tracking-wide">
                      ADMIN
                    </span>
                  )}
                  <TradingModeBadge />
                </div>
              </div>
            )}
          </div>

          {/* Nav groups */}
          <nav className="flex-1 px-3 py-2 overflow-y-auto scrollbar-thin space-y-1">
            <NavSection
              group={{ label: 'Portfolio', items: PORTFOLIO_ITEMS }}
              currentPath={location.pathname}
              onNavigate={close}
            />

            <div className="border-t border-gray-100 mt-1" />

            <NavSection
              group={{ label: 'Analysis', items: analysisItems }}
              currentPath={location.pathname}
              onNavigate={close}
            />

            {/* PB-1: one product-level link out, in place of the four research pages Trade used
                to embed. An `<a>`, not a `<Link>` — Lab is a different origin served by a
                different process, so router navigation would be wrong here. */}
            {LAB_ORIGIN && (
              <a
                href={LAB_ORIGIN}
                target="_blank"
                rel="noopener noreferrer"
                onClick={close}
                className="flex items-center gap-3 px-3 py-2.5 rounded-lg font-medium transition-all duration-200 text-sm text-gray-700 hover:bg-emerald-50 hover:text-emerald-800"
              >
                <FlaskConical className="w-4 h-4 flex-shrink-0" />
                <span className="flex-1">Open PortfoliYo Lab</span>
                <ExternalLink className="w-3.5 h-3.5 flex-shrink-0 text-gray-400" />
              </a>
            )}

            {isAdmin && (
              <>
                <div className="border-t border-gray-100 mt-1" />
                <NavSection
                  group={{ label: 'Admin', items: ADMIN_ITEMS }}
                  currentPath={location.pathname}
                  onNavigate={close}
                />
              </>
            )}
          </nav>

          {/* Footer */}
          <div className="p-3 border-t border-gray-200 space-y-1">
            <Link
              to="/auth"
              onClick={close}
              className={`flex items-center gap-3 px-3 py-2.5 rounded-lg font-medium transition-all duration-200 text-sm ${
                brokerAuthStatus?.is_authenticated
                  ? 'bg-emerald-700 text-white shadow-md hover:bg-emerald-800'
                  : 'bg-amber-500 text-white shadow-md hover:bg-amber-600'
              }`}
            >
              <div className="w-2 h-2 rounded-full bg-white flex-shrink-0" />
              <LogIn className="w-4 h-4 flex-shrink-0" />
              <span>{brokerAuthStatus?.is_authenticated ? 'Broker Connected' : 'Connect Broker'}</span>
            </Link>

            <button
              onClick={handleLogout}
              className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg font-medium transition-all duration-200 text-sm text-gray-700 hover:bg-red-50 hover:text-red-700"
            >
              <LogOut className="w-4 h-4 flex-shrink-0" />
              <span>Logout</span>
            </button>
          </div>
        </div>
      </aside>

      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-gray-900/20 backdrop-blur-sm z-30 lg:hidden transition-opacity"
          onClick={close}
        />
      )}

      <main className="lg:pl-64 min-h-screen">
        <MarketClosureTicker />
        {brokerAuthStatus && !brokerAuthStatus.is_authenticated && (
          <div className="bg-amber-50 border-b border-amber-200 px-4 py-4 lg:px-8">
            <div className="flex items-center justify-between max-w-7xl mx-auto">
              <div className="flex items-center gap-3 text-amber-900">
                <div className="w-10 h-10 rounded-lg bg-amber-500 flex items-center justify-center shadow-md">
                  <LogIn className="w-5 h-5 text-white" />
                </div>
                <div>
                  <span className="font-semibold text-base">
                    {brokerAuthStatus.needs_reauth ? 'Broker re-authentication required' : 'Broker not connected'}
                  </span>
                  <p className="text-sm text-amber-700">Connect your broker to enable live trading</p>
                </div>
              </div>
              <Link to="/auth" className="inline-flex items-center justify-center gap-2 px-5 py-2.5 text-sm font-semibold rounded-lg bg-emerald-700 text-white shadow-md hover:bg-emerald-800 transition-all">
                Connect Broker
              </Link>
            </div>
          </div>
        )}

        <div className="p-4 lg:p-8 xl:p-10 max-w-7xl mx-auto">
          {children}
        </div>
      </main>
    </div>
  );
}
